/**
 * Place一覧の仮想リスト
 *
 * BottomSheet の中で使うため、自前ではスクロールせず最寄りのスクロール祖先（シートの本文）に乗る。
 * 一覧より上にある検索欄・フィルタの高さは scrollMargin として測り、表示範囲の計算に含める。
 */

import { useVirtualizer } from '@tanstack/react-virtual'
import { useLayoutEffect, useRef, useState } from 'react'

import type { Place } from '@/api/types/overland'

import { PlaceListRow, type PlaceListRowHandlers } from './place-list-row'

/** 1 行の見積もり高さ（描画後に measureElement で実測し直す） */
const ESTIMATED_ROW_HEIGHT = 84
const ROW_GAP = 8

type PlaceVirtualListProps = PlaceListRowHandlers & {
  places: Place[]
  isSelectMode: boolean
  selectedIds: Set<number>
}

const findScrollParent = (element: HTMLElement): HTMLElement | null => {
  let node = element.parentElement
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}

export function PlaceVirtualList({ places, isSelectMode, selectedIds, ...handlers }: PlaceVirtualListProps) {
  'use no memo' // TanStack Virtual は React Compiler と非互換なため memoization をスキップ
  const listRef = useRef<HTMLDivElement | null>(null)
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return
    const scroller = findScrollParent(list)
    setScrollElement(scroller)
    if (!scroller) return

    const updateScrollMargin = () => {
      // hidden で非表示中は位置が取れないので、再表示時の ResizeObserver 通知を待つ
      if (list.offsetParent === null) return
      const offset = list.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
      setScrollMargin(Math.round(offset))
    }
    updateScrollMargin()

    // 一覧の再表示や、上にあるフィルタ等の高さ変化で一覧の開始位置がずれるため追従する
    const observer = new ResizeObserver(updateScrollMargin)
    observer.observe(list)
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild)
    return () => observer.disconnect()
  }, [])

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual は React Compiler 不対応、上記 'use no memo' で memoize 除外済み
  const virtualizer = useVirtualizer({
    count: places.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    getItemKey: (index) => places.at(index)?.id ?? index,
    overscan: 8,
    gap: ROW_GAP,
    scrollMargin,
  })

  return (
    <div ref={listRef} className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((item) => {
        const place = places.at(item.index)
        if (!place) return null
        return (
          <div
            key={item.key}
            ref={virtualizer.measureElement}
            data-index={item.index}
            className="absolute top-0 left-0 w-full"
            style={{ transform: `translateY(${item.start - scrollMargin}px)` }}
          >
            <PlaceListRow
              place={place}
              isSelectMode={isSelectMode}
              isSelected={selectedIds.has(place.id)}
              {...handlers}
            />
          </div>
        )
      })}
    </div>
  )
}
